import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import { simpleTableHeader } from "models/interfaces";
import { useEffect, useState } from "react";
import {
  createPasskey,
  deletePasskey,
  editPasskeyName,
  fetchPasskeys,
  getCredentialCreationOptions,
} from "../SettingsService";
import notifiers from "global/constants/NotificationConstants";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomInput from "global/components/CustomInput/CustomInput";
import strings from "global/constants/StringConstants";
import EditIcon from "assets/icons1/EditIcon.svg";
import DeleteIcon from "assets/icons1/Delete_Icon.svg";

const passkeyListTableHeader: simpleTableHeader[] = [
  {
    name: "Display Name",
    field: "displayName",
    align: "left",
  },
  {
    name: "User Name",
    field: "username",
    align: "left",
  },
  {
    name: "Action",
    field: "action",
    align: "right",
    styles: { paddingRight: "48px" },
  },
];

const Passkeys = () => {
  const theme = useTheme();
  const [loader, setLoader] = useState<boolean>(false);
  const [passKeys, setPassKeys] = useState<any[]>([]);
  const [passkeyConfigs, setPasskeyConfigs] = useState<any>({
    credentialOptions: null,
    passKeys: [],
    passKeyData: [],
    openCreatePasskeyDialog: false,
    displayName: "",
    error: "",
    isEdit: false,
    isDelete: false,
  });

  useEffect(() => {
    getPasskeys();
  }, []);

  const getPasskeys = async () => {
    try {
      setLoader(true);
      const response = await fetchPasskeys();
      setPassKeys(response);
      getFormattedTableRow(response);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleDialogClose = () => {
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      displayName: "",
      openCreatePasskeyDialog: false,
      error: "",
      isEdit: false,
      isDelete: false,
    }));
  };

  const handleDialogOpen = () => {
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      isEdit: false,
      isDelete: false,
      error: "",
      displayName: "",
      openCreatePasskeyDialog: true,
    }));
  };

  const updatePasskeyHandler = (displayName: string) => {
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      openCreatePasskeyDialog: true,
      isEdit: true,
      isDelete: false,
      displayName,
    }));
  };

  const deletePasskeyHandler = (displayName: string) => {
    setPasskeyConfigs((prevState: any) => ({
      ...prevState,
      isDelete: true,
      openCreatePasskeyDialog: true,
      isEdit: false,
      displayName,
    }));
  };

  const getFormattedTableRow = (passkeys: any[]) => {
    const formattedRows = passkeys?.map((passkey: any) => {
      return {
        displayName: passkey.displayName,
        username: passkey.username,
        action: (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Tooltip title="Edit Passkey" placement="top" arrow>
              <IconButton
                onClick={() => {
                  setPasskeyConfigs((prevState: any) => ({
                    ...prevState,
                    passKeyData: passkey,
                  }));
                  updatePasskeyHandler(passkey.displayName);
                }}
              >
                <img src={EditIcon} alt="Edit Icon" width="28px" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => {
                setPasskeyConfigs((prevState: any) => ({
                  ...prevState,
                  passKeyData: passkey,
                }));
                deletePasskeyHandler(passkey.displayName);
              }}
            >
              <img src={DeleteIcon} alt="Delete Icon" />
            </IconButton>
          </Box>
        ),
      };
    });
    setPassKeys(formattedRows);
  };

  const getPasskeyHeader = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography variant="h5" color="text.primary">
            Passkeys
          </Typography>

          <Button
            id="contact_add_btn"
            variant="outlined"
            disabled={loader}
            onClick={() => handleDialogOpen()}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            Create passkey
          </Button>
        </Stack>
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={passkeyListTableHeader}
              rows={passKeys}
              isLoading={loader}
              showPagination={false}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  const getDialogTitleContent = () => {
    if (passkeyConfigs.isEdit) {
      return "Update";
    } else if (passkeyConfigs.isDelete) {
      return "Delete";
    } else {
      return "Create";
    }
  };
  const updateDialogTitleContent = () => {
    return (
      <Typography
        variant="h4"
        color={theme.palette.primary.main}
      >{`${getDialogTitleContent()} Passkey`}</Typography>
    );
  };

  const getDialogBody = () => {
    return (
      <Stack direction="column" mt={1} textAlign="center">
        <Typography
          variant="h4"
          color={theme.palette.primary.main}
        >{`${getDialogTitleContent()} Passkey`}</Typography>
        <>
          {!passkeyConfigs.isDelete ? (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomInput
                  id="passkey_name"
                  name="addPasskeyName"
                  propsToInputElement={{
                    maxLength: strings.TAGS_LIMIT,
                  }}
                  placeHolder="Enter passkey name"
                  onChange={(event: any) =>
                    setPasskeyConfigs((prevState: any) => ({
                      ...prevState,
                      error: event.target.value ? "" : prevState.error,
                      displayName: event.target.value,
                    }))
                  }
                  value={passkeyConfigs.displayName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span style={{ fontSize: "0.75rem" }}>{passkeyConfigs?.displayName.length} / 25</span>
                      </InputAdornment>
                    ),
                  }}
                  error={
                    !isTruthy(passkeyConfigs.displayName) &&
                    passkeyConfigs.error
                  }
                  helperText={
                    !isTruthy(passkeyConfigs.displayName)
                      ? passkeyConfigs.error
                      : ""
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <Typography mt={1}>
              {`${"Are you sure you want to delete "}`}
              <span style={{ fontWeight: 700 }}>
                {passkeyConfigs.displayName} passkey ?
              </span>
            </Typography>
          )}
        </>
      </Stack>
    );
  };

  const handleUpdatePasskey = async () => {
    try {
      if (isTruthy(passkeyConfigs.passKeyData)) {
        setLoader(true);
        const response = await editPasskeyName({
          ...passkeyConfigs.passKeyData,
          displayName: passkeyConfigs.displayName,
        });
        openSuccessNotification(response.message);
        handleDialogClose();
        await getPasskeys();
      }
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleCreatePasskey = async () => {
    try {
      if (handleValidation() && isTruthy(passkeyConfigs.displayName)) {
        setLoader(true);
        const options = await getCredentialCreationOptions(
          passkeyConfigs.displayName
        );
        if (!options || options instanceof Error) {
          throw new Error("Failed to retrieve credential creation options");
        }
        setPasskeyConfigs((prevState: any) => ({
          ...prevState,
          credentialOptions: options,
          openCreatePasskeyDialog: false,
        }));
        const response = await createPasskey(options);
        openSuccessNotification(response.message);
        getPasskeys();
      }
    } catch (error: any) {
      setLoader(false);
      if (
        error.message ===
        "The user attempted to register an authenticator that contains one of the credentials already registered with the relying party."
      ) {
        openErrorNotification(
          "You have already added this device. Please use a different device and try again."
        );
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setLoader(false);
    }
  };

  const handleDeletePasskey = async () => {
    try {
      setLoader(true);
      const response = await deletePasskey(passkeyConfigs.passKeyData);
      handleDialogClose();
      openSuccessNotification(response.message);
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  const handleValidation = () => {
    if (!passkeyConfigs.displayName) {
      setPasskeyConfigs((prevState: any) => ({
        ...prevState,
        error: "Please enter passkey name",
      }));
      return false;
    }
    return true;
  };

  const getPasskeyActionFunction = async () => {
    if (!handleValidation()) {
      return;
    }

    if (passkeyConfigs.isEdit) {
      await handleUpdatePasskey();
    } else if (passkeyConfigs.isDelete) {
      await handleDeletePasskey();
    } else {
      await handleCreatePasskey();
    }
  };

  const getFooter = () => {
    return (
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1}
      >
        <Button
          id="delete_comment_no"
          size="small"
          variant="outlined"
          onClick={() => handleDialogClose()}
          disabled={loader}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={loader}
          onClick={async () => {
            await getPasskeyActionFunction();
            if (handleValidation()) {
              handleDialogClose();
              await getPasskeys();
            }
          }}
          id="apply_button"
        >
          {getDialogTitleContent()}
        </Button>
      </Stack>
    );
  };

  return (
    <Box mt={3}>
      {getPasskeyHeader()}
      <CustomDialog
        isDialogOpen={passkeyConfigs.openCreatePasskeyDialog}
        closable
        closeButtonVisibility
        handleDialogClose={() => handleDialogClose()}
        // title={updateDialogTitleContent()}
        body={getDialogBody()}
        footer={getFooter()}
        width="sm"
      />
    </Box>
  );
};

export default Passkeys;
